import { useEffect, useRef, useState } from 'react';
import { useNavParams } from 'hooks';

import { useLatestRef } from 'components/shared/hooks/useLatestRef';

import type { Header, TabType } from '../types';

/**
 * This hook is responsible for the two levels navigation:
 *
 * 1. Level: tabIndex
 * 2. Level: tabbedElement
 *
 * It also listens to hash changes and sets the correct navigation element based on the result.
 */
const useTabNavigation = (
  defaultTabIndex: number,
  defaultTabbedElement: TabType,
  headers: Header[],
  views: TabType[],
  /**
   * The unfiltered view config. Only read to tell a page param that no
   * configuration knows apart from one that grants filtered out: the first can
   * never become resolvable, the second can. Defaults to `views` for callers that
   * hand over an unfiltered set anyway.
   */
  configuredViews: TabType[] = views
) => {
  'use memo';

  const [tabIndex, setTabIndex] = useState<number>(defaultTabIndex);
  const [tabbedElement, setTabbedElement] =
    useState<TabType>(defaultTabbedElement);

  const { params, setParams } = useNavParams();

  const defaultHeader = headers[defaultTabIndex];
  const defaultView =
    views.find((view) => view.parent === defaultHeader?.subHeaderName) ??
    views[0];

  // Besides the hash, the effect below reacts to the *contents* of the header and
  // view sets: those change once grants arrive (`SET_USER_DETAILS`), which can make
  // a view resolvable that was not before. Their identity is deliberately not a
  // dependency - the caller rebuilds both arrays whenever `t` or `grants` change,
  // and re-running on that throws away a selection made through the setters below.
  const viewNames = views.map(({ name }) => name).join();
  const headerNames = headers.map(({ subHeaderName }) => subHeaderName).join();
  const configuredViewNames = configuredViews.map(({ name }) => name).join();

  // Read by the effect, never watched: `setParams` is a new function on every
  // render, and the arrays are needed in full while only their contents above
  // decide whether the effect has to run again.
  const latest = useLatestRef({
    views,
    headers,
    configuredViews,
    defaultView,
    setParams
  });

  // The default page param is written at most once per mount. `params.page` lags
  // the hash by a `hashchange` tick, so an effect run inside that window still sees
  // "no page param" and would write the default view over the one that was just
  // restored from session storage or navigated to.
  const defaultParamWritten = useRef(false);

  /* Listen on hash change and set the tabbed element based on that*/
  useEffect(() => {
    const { views, headers, configuredViews, defaultView, setParams } =
      latest.current;

    const requested = params?.page?.toUpperCase();
    const requestedView = requested
      ? views.find((tableView) => tableView.name === requested)
      : undefined;
    const tableView = requestedView ?? defaultView;

    if (!tableView) return;

    setTabIndex(
      headers.findIndex(
        ({ subHeaderName }) => subHeaderName === tableView.parent
      )
    );
    setTabbedElement(tableView);

    // Nothing in the hash to go on, so the default view was selected: publish it,
    // at most once per mount.
    if (!requested) {
      if (!defaultParamWritten.current) {
        // Only counts as written once it landed: `setParams` drops the write when
        // the hash has moved on to a route this hook no longer owns (#2145), and
        // that is not a reason to stop trying.
        defaultParamWritten.current = setParams({
          page: tableView.name.toLowerCase()
        });
      }
      return;
    }

    if (requestedView) return;

    // The param named a view we could not resolve, and only one of the two reasons
    // for that is ours to correct. A view no configuration knows (renamed, removed,
    // an old bookmark) can never become resolvable, and `TabsRouter` has no route
    // for it: point the hash at the view we actually selected, or the screen keeps
    // a filled tab bar above an empty content area. A view that *is* configured but
    // was filtered out is the grants case - grants can still arrive and make it
    // resolvable - so leave the hash alone there and let `TabsRouter`'s fallback
    // route render the selected view in the meantime.
    if (!configuredViews.some((tableView) => tableView.name === requested)) {
      setParams({ page: tableView.name.toLowerCase() });
    }
  }, [params?.page, viewNames, headerNames, configuredViewNames, latest]);

  return {
    tabbedElement,
    setTabbedElement,
    tabIndex,
    setTabIndex
  };
};

export default useTabNavigation;
